import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import NotWhileActingAs from './components/NotWhileActingAs';
import AppLayout from './components/AppLayout';

// Auth pages (small, loaded eagerly for fast first paint)
import LoginPage from './features/Auth/LoginPage';
import SetPasswordPage from './features/Auth/SetPasswordPage';
import ForceChangePasswordPage from './features/Auth/ForceChangePasswordPage';
import GroupSelectorPage from './features/Auth/GroupSelectorPage';

// Lazy-loaded pages
const AdminDashboard = lazy(() => import('./features/Admin/AdminDashboard'));
const PricingTiersPage = lazy(() => import('./features/Admin/PricingTiersPage'));
const MemberDashboard = lazy(() => import('./features/Member/MemberDashboard'));
const RecommendationList = lazy(() => import('./features/Member/RecommendationList'));
const RecommendationForm = lazy(() => import('./features/Member/RecommendationForm'));
const GroupMembersList = lazy(() => import('./features/GroupAdmin/GroupMembersList'));
const AddMemberPage = lazy(() => import('./features/GroupAdmin/AddMemberPage'));
const MeetingPage = lazy(() => import('./features/GroupAdmin/MeetingPage'));
const CotisationPage = lazy(() => import('./features/GroupAdmin/CotisationPage'));
const NewMeetingPage = lazy(() => import('./features/GroupAdmin/NewMeetingPage'));
const TeteATeteListPage = lazy(() => import('./features/Member/TeteATeteListPage'));
const NewTeteATetePage = lazy(() => import('./features/Member/NewTeteATetePage'));
const GroupFormPage = lazy(() => import('./features/SuperAdmin/GroupFormPage'));
const EmailTemplatesPage = lazy(() => import('./features/SuperAdmin/EmailTemplatesPage'));
const GroupEmailTemplatesPage = lazy(() => import('./features/GroupAdmin/EmailTemplatesPage'));
const GroupSettingsPage = lazy(() => import('./features/GroupAdmin/GroupSettingsPage'));
const EditMemberPage = lazy(() => import('./features/GroupAdmin/EditMemberPage'));
const ProfilePage = lazy(() => import('./features/Profile/ProfilePage'));
const AdminGroupsPage = lazy(() => import('./features/Admin/AdminGroupsPage'));
const AdminMembersPage = lazy(() => import('./features/Admin/AdminMembersPage'));
const SuperAdminNotificationSettings = lazy(() => import('./features/SuperAdmin/NotificationSettingsPage'));
const SmsManagementPage = lazy(() => import('./features/SuperAdmin/SmsManagementPage'));
const TestimonialsManagementPage = lazy(() => import('./features/SuperAdmin/TestimonialsManagementPage'));
const GroupAdminNotificationSettings = lazy(() => import('./features/GroupAdmin/NotificationSettingsPage'));
const AboutPage = lazy(() => import('./features/About/AboutPage'));
const MemberDirectoryPage = lazy(() => import('./features/Member/MemberDirectoryPage'));
const MemberDetailPage = lazy(() => import('./features/Member/MemberDetailPage'));
const TestimonialPage = lazy(() => import('./features/Member/TestimonialPage'));
const MessagesPage = lazy(() => import('./features/Member/MessagesPage'));
const AdminEventListPage = lazy(() => import('./features/GroupAdmin/EventListPage'));
const AdminEventFormPage = lazy(() => import('./features/GroupAdmin/EventFormPage'));
const AdminEventDetailPage = lazy(() => import('./features/GroupAdmin/EventDetailPage'));
const MemberEventListPage = lazy(() => import('./features/Member/EventListPage'));
const MemberEventDetailPage = lazy(() => import('./features/Member/EventDetailPage'));

// Marketing pages (public, no auth required)
const LandingPage = lazy(() => import('./features/Marketing/LandingPage').then(m => ({ default: m.LandingPage })));

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
    </div>
  );
}

function RoleBasedIndex() {
  const { user } = useAuth();
  if (user?.role === 'super_admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'group_admin' || user?.role === 'supervisor') return <Navigate to="/group/members" replace />;
  return <MemberDashboard />;
}

function SmartIndex() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <LandingPage />;
  if (user.role === 'super_admin') return <Navigate to="/admin" replace />;
  if (user.role === 'group_admin' || user.role === 'supervisor') return <Navigate to="/group/members" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <Router basename={import.meta.env.BASE_URL}>
      <ScrollToTop />
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Root — smart index (handles auth check itself) */}
            <Route path="/" element={<SmartIndex />} />

            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/force-change-password" element={<ProtectedRoute><NotWhileActingAs><ForceChangePasswordPage /></NotWhileActingAs></ProtectedRoute>} />
            <Route path="/select-group" element={<ProtectedRoute><NotWhileActingAs><GroupSelectorPage /></NotWhileActingAs></ProtectedRoute>} />

            {/* Authenticated routes inside AppLayout */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="recommendations" element={<RecommendationList />} />
              <Route path="recommendations/new" element={<RecommendationForm />} />

              {/* Member tete-a-tete routes */}
              <Route path="tete-a-tete" element={<TeteATeteListPage />} />
              <Route path="tete-a-tete/nouveau" element={<NewTeteATetePage />} />

              {/* Member directory (annuaire) */}
              <Route path="annuaire" element={<MemberDirectoryPage />} />
              <Route path="annuaire/:id" element={<MemberDetailPage />} />

              {/* Messages */}
              <Route path="messages" element={
                <ProtectedRoute roles={['member', 'group_admin']}>
                  <NotWhileActingAs>
                    <MessagesPage />
                  </NotWhileActingAs>
                </ProtectedRoute>
              } />

              {/* Testimonials (all authenticated users) */}
              <Route path="temoignages" element={<TestimonialPage />} />

              {/* Group Admin routes — also accessible to supervisor (read-only gating handled per-page) */}
              <Route path="group" element={<Navigate to="/group/members" replace />} />
              <Route path="group/settings" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><GroupSettingsPage /></ProtectedRoute>} />
              <Route path="group/members/new" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><AddMemberPage /></ProtectedRoute>} />
              <Route path="group/members" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><GroupMembersList /></ProtectedRoute>} />
              <Route path="group/meetings" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><MeetingPage /></ProtectedRoute>} />
              <Route path="group/meetings/new" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><NewMeetingPage /></ProtectedRoute>} />
              <Route path="group/cotisations" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><CotisationPage /></ProtectedRoute>} />
              <Route path="group/members/:id/edit" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><EditMemberPage /></ProtectedRoute>} />
              <Route path="group/email-templates" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><GroupEmailTemplatesPage /></ProtectedRoute>} />
              <Route path="group/notification-settings" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><GroupAdminNotificationSettings /></ProtectedRoute>} />

              {/* Admin events */}
              <Route path="group/events" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><AdminEventListPage /></ProtectedRoute>} />
              <Route path="group/events/new" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><AdminEventFormPage /></ProtectedRoute>} />
              <Route path="group/events/:eventId" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><AdminEventDetailPage /></ProtectedRoute>} />
              <Route path="group/events/:eventId/edit" element={<ProtectedRoute roles={['group_admin', 'super_admin', 'supervisor']}><AdminEventFormPage /></ProtectedRoute>} />

              {/* Member events */}
              <Route path="events" element={<MemberEventListPage />} />
              <Route path="events/:eventId" element={<MemberEventDetailPage />} />

              {/* Dashboard alias for group_admin member view */}
              <Route path="dashboard" element={<ProtectedRoute roles={['group_admin', 'member']}><MemberDashboard /></ProtectedRoute>} />

              {/* Profile route (all authenticated users) */}
              <Route path="profile" element={<ProfilePage />} />

              {/* About page (all authenticated users) */}
              <Route path="about" element={<AboutPage />} />

              {/* Super Admin routes */}
              <Route path="admin" element={<ProtectedRoute roles={['super_admin']}><AdminDashboard /></ProtectedRoute>} />
              <Route path="admin/groups" element={<ProtectedRoute roles={['super_admin']}><AdminGroupsPage /></ProtectedRoute>} />
              <Route path="admin/membres" element={<ProtectedRoute roles={['super_admin']}><AdminMembersPage /></ProtectedRoute>} />
              <Route path="admin/tarifs" element={<ProtectedRoute roles={['super_admin']}><PricingTiersPage /></ProtectedRoute>} />
              <Route path="admin/email-templates" element={<ProtectedRoute roles={['super_admin']}><EmailTemplatesPage /></ProtectedRoute>} />
              <Route path="admin/notification-settings" element={<ProtectedRoute roles={['super_admin']}><SuperAdminNotificationSettings /></ProtectedRoute>} />
              <Route path="admin/sms" element={<ProtectedRoute roles={['super_admin']}><SmsManagementPage /></ProtectedRoute>} />
              <Route path="admin/temoignages" element={<ProtectedRoute roles={['super_admin']}><TestimonialsManagementPage /></ProtectedRoute>} />
              <Route path="admin/groups/new" element={<ProtectedRoute roles={['super_admin']}><GroupFormPage /></ProtectedRoute>} />
              <Route path="admin/groups/:id/edit" element={<ProtectedRoute roles={['super_admin']}><GroupFormPage /></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  );
}

export default App;
